import ScrollMagic from 'scrollmagic';
import gsap from 'gsap';
import View from './View';
import scrollController from './../showup/ScrollController';
import { getWebflowAnimationProps, parseProps } from './../lib/dataTweenParser';
import { getScrollMagicSceneProps } from './../lib/dataScrollMagicParser';

// data-webview-... (aswap show / hide)
const DATA_ATTR_WEBVIEW_INIT = "webview-initial";
const DATA_ATTR_WEBVIEW_SHOW = "webview-show";
const DATA_ATTR_WEBVIEW_HIDE = "webview-hide";
// data-webscroll-... (scroll magic)
const DATA_ATTR_WEBSCROLL_INIT = "webscroll-initial";
const DATA_ATTR_WEBSCROLL_SHOW = "webscroll-show";
const DATA_ATTR_WEBSCROLL_HIDE = "webscroll-hide";
// data-stagger-... (stagger - lists)
const DATA_ATTR_STAGGER_INIT = "stagger-initial";
const DATA_ATTR_STAGGER_SHOW = "stagger-show";
const DATA_ATTR_STAGGER_HIDE = "stagger-hide";
const DATA_ATTR_SETUP = "webset";
const PROP_KEY_WEBSCROLL_FIRE_ONCE = "fireOnce";

export const CLASS_NAME_WEBSCROLL_FIRED = "webflow-scroll-fired";

class WebflowView extends View {

  constructor(id) {
    super(id);
    this.scenes = [];
  }
  /**
  * prepare (before show)
  **/
  prepare(container) {
    super.prepare(container);
  }
  /**
  * animate in (show)
  **/  
  show(container) {
    this.container = container;
    this.hideScrollElements();
    super.show(container);
    // show Aswap webviews
    this.webViewsShow();
    // show stagger elements
    this.staggerShow();
    // build scrollmagic scenes
    this.buildScrollmagicScenes();
    // add event listeners
    this.addEventListeners();
  }
  /**
  * animate out (hide)
  **/
  hide(container) {
    super.hide(container);
    // hide Aswap webviews
    this.webViewsHide();
    // hide stagger elements
    this.staggerHide();
    // hide scrollmagic scenes
    this.hideScrollmagicScenes();
  }
  /**
  * animate out (hide)
  **/
  hidden(container) {
    super.hidden(container);
    this.removeEventListeners();
    this.destroyScenes();
  }
  /**
   * ##################
   * ### DOM events ###
   * ##################
   */
  /**
   * Class event handlers
   */
  handleEvent(e) {
    switch (e.type) {
      case 'resize':
        this.onWindowUpdate(e);
        break;
      case 'DOMContentLoaded':
        this.onWindowUpdate(e);
        break;
    }
  }
  /**
   * Add event listeners
   */
  addEventListeners() {
    window.addEventListener('resize', this);
    document.addEventListener('DOMContentLoaded', this);
  }
  /**
   * Remove event listeners
   */
  removeEventListeners() {
    window.removeEventListener('resize', this);
    document.removeEventListener('DOMContentLoaded', this);
  }
  onWindowUpdate(e) {
    if (this.scenes.length > 0) {
      scrollController.refresh();
    }
  }
  /**
   * ############
   * ### SHOW ###
   * ############
   */
  // ### ASwap Show / Hide ###
  // show webviews
  webViewsShow() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_WEBVIEW_SHOW + ']')];
    list.forEach(this.webViewElementShow.bind(this));
  }
  // showe webview element
  webViewElementShow(el, index) {
    const propsInitial = this.getTweenProps(el, DATA_ATTR_WEBVIEW_INIT);
    const propsShow = this.getTweenProps(el, DATA_ATTR_WEBVIEW_SHOW);
    if (propsInitial && propsShow) {
      gsap.killTweensOf(el);
      gsap.set(el, {...propsInitial.tween});
      gsap.to(el, {duration: propsShow.time, ...propsShow.tween});
    }
  }
  // ### Stagger ###
  // Stagger (lists) show
  staggerShow() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_STAGGER_SHOW + ']')];
    list.forEach(this.staggerListShow.bind(this));
  }
  staggerListShow(el, index) {
    const { selector } = parseProps(el.dataset[DATA_ATTR_SETUP]);
    if (!selector)  {
      console.warn("Unknown selector for webflow stagger", el);
      return;
    }
    const items = [...el.querySelectorAll(selector)];
    const propsInit = this.getTweenProps(el, DATA_ATTR_STAGGER_INIT);
    const propsShow = this.getTweenProps(el, DATA_ATTR_STAGGER_SHOW);
    if (items.length && propsInit && propsShow) {
      gsap.killTweensOf(items);
      gsap.set(items, {...propsInit.tween});
      gsap.to(items, {duration: propsShow.time, ...propsShow.tween});
    }
  }
  // ### ScrollMagic ###
  // hide scenes (before show animation)
  hideScrollElements() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_WEBSCROLL_SHOW + ']')];
    list.forEach(this.hideScrollElement.bind(this));
  }
  hideScrollElement(el, index) {
    const propsInitial = this.getTweenProps(el, DATA_ATTR_WEBSCROLL_INIT);
    if (propsInitial) {
      gsap.killTweensOf(el);
      gsap.set(el, {...propsInitial.tween});
    }
  }
  /**
   * Build scenes for scrollmagic
   */
  buildScrollmagicScenes() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_WEBSCROLL_SHOW + ']')];
    list.forEach(this.buildScrollmagicScene.bind(this));
  }
  /**
   * Build scroll magic scene
   * @param {DOM} el 
   * @param {Int} index 
   */
  buildScrollmagicScene(el, index) {
    const propsInitial = this.getTweenProps(el, DATA_ATTR_WEBSCROLL_INIT);
    const propsShow = this.getTweenProps(el, DATA_ATTR_WEBSCROLL_SHOW);
    const setup = parseProps(el.dataset[DATA_ATTR_SETUP]);
    const fireOnce = setup[PROP_KEY_WEBSCROLL_FIRE_ONCE] === "false" ? false : true;
    if (propsInitial && propsShow) {
      const smsp = getScrollMagicSceneProps(el, setup);
      const scene = new ScrollMagic.Scene(smsp);
      scene.on("start", (e) => {
        if (fireOnce && el.classList.contains(CLASS_NAME_WEBSCROLL_FIRED)) {
          return;
        } 
        if (e.scrollDirection === "FORWARD") {
          gsap.killTweensOf(el);
          gsap.set(el, {...propsInitial.tween});
          gsap.to(el, {duration: propsShow.time, ...propsShow.tween});
          el.classList.add(CLASS_NAME_WEBSCROLL_FIRED);
        }
      });
      scene.addTo(scrollController.get());
      this.scenes.push(scene);
    }
  }
  /**
   * ############
   * ### HIDE ###
   * ############
   */
  // ### ASwap Show / Hide ###
  // hide webviews
  webViewsHide() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_WEBVIEW_HIDE + ']')];
    list.forEach(this.webViewsElementHide.bind(this));
  }
  // showe webview element
  webViewsElementHide(el, index) {
    const propsHide = this.getTweenProps(el, DATA_ATTR_WEBVIEW_HIDE);
    if (propsHide) {
      gsap.killTweensOf(el);
      gsap.to(el, {duration: propsHide.time, ...propsHide.tween});
    }
  }
  // ### Stagger ###
  // Hide stagger
  staggerHide() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_STAGGER_HIDE + ']')];
    list.forEach(this.staggerListHide.bind(this));
  }
  // Hide stagger element
  staggerListHide(el, index) {
    const { selector } = parseProps(el.dataset[DATA_ATTR_SETUP]);
    if (!selector)  {
      console.warn("Unknown selector for webflow stagger", el);
      return;
    }
    const items = [...el.querySelectorAll(selector)];
    const propsHide = this.getTweenProps(el, DATA_ATTR_STAGGER_HIDE);
    if (items.length && propsHide) {
      gsap.killTweensOf(items);
      gsap.to(items, {duration: propsHide.time, ...propsHide.tween});
    }
  }
  // ### ScrollMagic ###
  /**
   * hide scenes
   */
  hideScrollmagicScenes() {
    if (!this.container) {
      console.warn("Unknown container", this);
      return;
    }
    const list = [...this.container.querySelectorAll('*[data-' + DATA_ATTR_WEBSCROLL_HIDE + ']')];
    list.forEach(this.hideScrollmagicElement.bind(this));
  }
  /**
   * Hide element
   * @param {DOM} el 
   * @param {Int} index 
   */
  hideScrollmagicElement(el, index) {
    const propsHide = this.getTweenProps(el, DATA_ATTR_WEBSCROLL_HIDE);
    if (propsHide) {
      gsap.killTweensOf(el);
      gsap.to(el, { duration: propsHide.time, ...propsHide.tween});
    }
  }
  // ## Destroy scrollmagic scenes
  destroyScenes() {
    this.scenes.forEach((scene) => {
      scene.destroy();
      scene = null;
    })
    this.scenes = [];
  }
  /**
   * ###############
   * ### HELPERS ###
   * ###############
   */
  // get animation props
  getTweenProps(el, name) {
    const dataAttrName = this.getDataAttributeName(name);
    return getWebflowAnimationProps(el.dataset[dataAttrName]);
  }
  // replace data-x with dataX
  getDataAttributeName(name) {
    return name.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); });
  }

}
export default WebflowView